'use client';

import React from 'react';
import { atom, useAtom, useStore } from 'jotai';
import { useHydrateAtoms } from 'jotai/utils';

export const pathPrefixAtom = atom('');
export const subdomainAtom = atom('');

type HydrateBlogAtomsProps = {
  subdomain: string;
  urlPrefix: string | null;
  hasPassword: boolean;
};

export function HydrateBlogAtoms({
  urlPrefix,
  subdomain,
  hasPassword,
}: HydrateBlogAtomsProps) {
  const store = useStore();
  // These pages are statically generated, so the server can't know which host
  // (Quotion subdomain vs. the customer's subdirectory) the request is for.
  // Render canonical absolute URLs on the server. This keeps links crawlable
  // when the canonical site lives below a path (for example quotion.co/blog).
  // The effect below converts them to a local prefix for client navigation.
  useHydrateAtoms(
    [
      [pathPrefixAtom, getServerPathPrefix({ urlPrefix, hasPassword })],
      [subdomainAtom, subdomain],
    ],
    {
      store,
    },
  );
  const [, setPathPrefix] = useAtom(pathPrefixAtom);
  React.useEffect(() => {
    setPathPrefix(
      getPathPrefix({ urlPrefix, host: location.host, hasPassword }),
    );
  }, [urlPrefix, hasPassword, setPathPrefix]);
  return <></>;
}

export function getServerPathPrefix({
  urlPrefix,
  hasPassword,
}: Pick<HydrateBlogAtomsProps, 'urlPrefix' | 'hasPassword'>) {
  const pwSuffix = hasPassword ? '/pw' : '';
  return `${urlPrefix || ''}${pwSuffix}`;
}

function getPathPrefix({
  urlPrefix,
  host,
  hasPassword,
}: {
  urlPrefix: string | null;
  host: string;
  hasPassword: boolean;
}) {
  const pwSuffix = hasPassword ? '/pw' : '';
  if (!urlPrefix) {
    return pwSuffix;
  }
  const url = new URL(urlPrefix);
  // `host` (location.host) may include a port; a URL hostname never does. Only
  // apply the subdirectory path when we're actually on the prefix host.
  const hostname = host.split(':')[0];
  const basePath = url.hostname === hostname ? url.pathname : '';
  return basePath + pwSuffix;
}
